import { useEffect, useRef, useState } from 'react';

interface CopyButtonProps {
  text: string;
  label: string;
  ariaLabel?: string;
  className?: string;
  /** Appelé à chaque clic, que la copie soit automatique ou manuelle. */
  onCopy?: () => void;
}

/**
 * Copie un texte dans le presse-papiers. Si le navigateur refuse, le texte est
 * affiché sélectionné dans une zone en lecture seule pour une copie manuelle.
 */
export function CopyButton({ text, label, ariaLabel, className, onCopy }: CopyButtonProps) {
  const [status, setStatus] = useState<'idle' | 'copied' | 'manual'>('idle');
  const manualRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (status !== 'copied') return;
    const timer = setTimeout(() => setStatus('idle'), 2000);
    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    if (status === 'manual') manualRef.current?.select();
  }, [status]);

  useEffect(() => {
    setStatus('idle');
  }, [text]);

  const onClick = async () => {
    try {
      if (!navigator.clipboard) throw new Error('Presse-papiers indisponible');
      await navigator.clipboard.writeText(text);
      setStatus('copied');
    } catch {
      setStatus('manual');
    }
    onCopy?.();
  };

  return (
    <>
      <button
        type="button"
        className={className ?? 'ci-btn ci-btn--sm'}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {status === 'copied' ? 'Copié' : label}
      </button>
      <span className="ci-sr-only" aria-live="polite">
        {status === 'copied' ? 'Texte copié.' : ''}
      </span>
      {status === 'manual' && (
        <div className="ci-copy-manual">
          <p role="status">Copie automatique impossible : le texte est sélectionné, utilisez Ctrl+C.</p>
          <textarea ref={manualRef} readOnly value={text} rows={5} aria-label="Texte à copier" />
        </div>
      )}
    </>
  );
}
